import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { I18NextService } from 'angular-i18next';
import { Angulartics2 } from 'angulartics2';
import { GlobalActions, REQUEST_SERVICE, RequestService, RouteActions, TypedFormGroup } from 'core';
import { pluck } from 'rxjs/operators';

declare type BackButtonType = 'requestList' | 'taskList' | 'planer' | 'none';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'back-to-registries-button',
    styleUrls: ['./back-to-registries-button.component.scss'],
    templateUrl: './back-to-registries-button.component.html'
})
export class BackToRegistriesButtonComponent implements OnInit, OnChanges {
    @Input()
    public requestState: CanonicalStateWithTraits | null = null;
    @Input()
    public form: TypedFormGroup<any>;
    @Input()
    public forceDisplayModal: boolean = false;
    @Input()
    public customModalMessage: string;
    public buttonTitle: string = '';
    public buttonTooltip: string = '';
    // При открытии заявки из внешней системы (planer, к примеру) можно передать этот параметр и при нажатии кнопки мы вернемся на него, а не главную системы заявок
    private returnUrl: string | null = null;
    private backButtonType: BackButtonType = 'none';

    constructor(
        private route: ActivatedRoute,
        private routerActions: RouteActions,
        private i18next: I18NextService,
        private angulartics2: Angulartics2,
        @Optional() @Inject(REQUEST_SERVICE) private requestService: RequestService
    ) {
    }

    public ngOnInit(): void {
        this.route.queryParams.pipe(pluck('returnurl')).subscribe(returnUrl => {
            this.returnUrl = returnUrl;
            this.calculateState();
        });
    }

    public closeForm(): void {
        if (this.requestState) {
            this.angulartics2.eventTrack.next({
                action: 'Close request',
                properties: {category: this.requestState.canonicalInfo.businessProcessType, label: this.requestState.canonicalInfo.correlationId}
            });
        }
        if ((this.form && this.form.enabled && this.form.dirty) || this.forceDisplayModal === true) {
            const displayCustomMessage = this.customModalMessage && this.customModalMessage.length > 0;
            GlobalActions.displayConfirm(displayCustomMessage ? this.customModalMessage : this.i18next.t('shared:modals.closeRequestFormDialogTitle'), () => {
                this.navigateToPreviousPage();
            });
        } else {
            this.navigateToPreviousPage();
        }
    }

    public ngOnChanges(): void {
        this.calculateState();
    }

    private calculateState(): void {
        const requestIsEditable =
            !!this.requestService && (this.requestState?.editTraits.canBeEditedByCurrentUser || this.requestState?.editTraits.currentUserCanApplyCarrierEdit);
        this.buttonTitle = this.i18next.t('shared:buttons.backToRequestList');
        this.buttonTooltip = requestIsEditable ? 'shared:buttons.cancelEditingAndReturnToRegistriesTooltip' : 'shared:buttons.backToRegistriesButtonTooltip';
        if (!this.returnUrl) {
            this.returnUrl = '/request-list';
            this.buttonTooltip = 'shared:buttons.backToRegistriesFromAccessDeniedButtonTooltip';
        } else if (this.returnUrl.toLowerCase().indexOf('planer') > -1) {
            this.backButtonType = 'planer';
            this.buttonTitle = this.i18next.t('shared:buttons.backToPlaner');
        } else if (this.returnUrl.toLowerCase().indexOf('department-task-list') > -1 && ETR.currentUserTraits.canSeeDepartmentTaskList) {
            this.backButtonType = 'taskList';
            this.buttonTitle = this.i18next.t('shared:buttons.backToTaskList');
        } else if (this.returnUrl.toLowerCase().indexOf('request-list') > -1) {
            this.backButtonType = 'requestList';
            this.buttonTitle = this.i18next.t('shared:buttons.backToRequestList');
        } else {
            this.buttonTooltip = 'shared:buttons.backToRegistriesFromAccessDeniedButtonTooltip';
        }
    }

    private navigateToPreviousPage(): void {
        switch (this.backButtonType) {
            case 'planer':
            case 'taskList':
            case 'requestList':
                this.routerActions.goToReturnUrl(this.returnUrl ?? null as unknown as string);
                break;

            default:
                this.routerActions.goToRoot();
        }
    }
}
